import React from "react";
import { Check } from "lucide-react";
import { LIMITS, listCategories, listItems } from "../../lib/roleLists";

// Old fields (teamRoleId, levelId, scheme/coverage, areaIds, situationIds, skillsNeededIds) stay on saved records; they are no longer asked.
export const EMPTY_ENTRY = { coachName: "", coachEmail: "", team: "", season: "", responsibilityIds: [], matchupIds: [], playTypeIds: [], minutes: "", earnMoreMinutes: "", comments: "" };

const Field = ({ label, children, hint }) => (
  <label className="block">
    <div className="text-[9px] uppercase tracking-[0.3em] text-[#a0a0a0] mb-1">{label}{hint && <span className="text-[#575757] normal-case tracking-normal ml-2">{hint}</span>}</div>
    {children}
  </label>
);
const inputCls = "w-full bg-[#141414] border border-white/10 text-white text-sm px-3 py-2 placeholder:text-[#575757] focus:outline-none focus:border-[var(--org-accent)]";

function Chip({ on, disabled, onClick, children, testid, title, retired }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled && !on} title={title} aria-pressed={on} data-testid={testid}
      className={`text-left px-3 py-2 border text-sm flex items-start gap-2 transition-colors ${on ? "border-[var(--org-accent)] bg-[#1a1a1a] text-white" : "border-white/10 bg-[#141414] text-[#c0c0c0] hover:border-white/30"} ${disabled && !on ? "opacity-40 cursor-not-allowed" : ""}`}>
      {on && <Check className="w-3.5 h-3.5 text-[var(--org-accent)] shrink-0 mt-0.5" />}<span>{children}{retired && <span className="text-[9px] uppercase tracking-[0.2em] text-[#757575] ml-1.5">(retired)</span>}</span>
    </button>
  );
}

// Multi-pick with optional max and optional Category grouping (CSV order). Retired items already chosen stay visible.
function MultiPick({ list, field, value, onChange, grouped, testid, helper }) {
  const max = LIMITS[field]; const full = max ? value.length >= max : false;
  const toggle = (id) => onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  const cats = grouped ? listCategories(list, value) : [""];
  return (
    <div className="space-y-3" data-testid={testid}>
      {cats.map(cat => (
        <div key={cat}>
          {cat && <div className="text-[9px] uppercase tracking-[0.35em] text-[#757575] mb-1.5">{cat}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {listItems(list, value).filter(i => !grouped || (i.category || "") === cat).map(i => (
              <Chip key={i.id} on={value.includes(i.id)} disabled={full} onClick={() => toggle(i.id)} testid={`${testid}-${i.id}`} title={!helper && i.description ? i.description : undefined} retired={i.status !== "active"}>
                {i.name}{helper && i.description && <span className="block text-[10px] text-[#757575]">{i.description.replace(/\s*Related actions:.*$/, "")}</span>}
              </Chip>
            ))}
          </div>
        </div>
      ))}
      <div className="text-[10px] text-[#757575]">{value.length}{max ? `/${max}` : ""} selected</div>
    </div>
  );
}

// Shared by the public coach page and the admin entry flow. 3 questions, ~2 minutes.
export function CoachRoleForm({ value, onChange, onSubmit, submitting, submitLabel = "Submit" }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const canSubmit = value.coachName.trim() && value.team.trim() && value.season.trim() && (value.responsibilityIds.length || (value.matchupIds || []).length || (value.playTypeIds || []).length);
  return (
    <form onSubmit={e => { e.preventDefault(); if (canSubmit) onSubmit(); }} className="space-y-8" data-testid="coach-role-form">
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Coach name"><input value={value.coachName} onChange={e => set("coachName", e.target.value)} className={inputCls} data-testid="cf-coach-name" required /></Field>
        <Field label="Email" hint="optional"><input type="email" value={value.coachEmail || ""} onChange={e => set("coachEmail", e.target.value)} className={inputCls} data-testid="cf-coach-email" /></Field>
        <Field label="Team"><input value={value.team} onChange={e => set("team", e.target.value)} className={inputCls} data-testid="cf-team" required /></Field>
        <Field label="Season"><input value={value.season} onChange={e => set("season", e.target.value)} placeholder="e.g. 2026-27" className={inputCls} data-testid="cf-season" required /></Field>
      </section>

      <section>
        <h3 className="text-sm font-bold text-white mb-1">1 · Role responsibilities</h3>
        <div className="text-[11px] text-[#a0a0a0] mb-3">The job the player is asked to do. Pick up to 3 — defense is covered by "Who does this player guard".</div>
        <MultiPick list="responsibility" field="responsibilityIds" value={value.responsibilityIds} onChange={v => set("responsibilityIds", v)} grouped testid="cf-responsibilities" />
      </section>

      <section>
        <h3 className="text-sm font-bold text-white mb-1">2 · Who does this player guard?</h3>
        <div className="text-[11px] text-[#a0a0a0] mb-3">Pick all that apply.</div>
        <MultiPick list="matchup" field="matchupIds" value={value.matchupIds || []} onChange={v => set("matchupIds", v)} testid="cf-matchups" />
      </section>

      <section>
        <h3 className="text-sm font-bold text-white mb-1">3 · Play types they're in most</h3>
        <div className="text-[11px] text-[#a0a0a0] mb-3">Pick up to 3.</div>
        <MultiPick list="play_type" field="playTypeIds" value={value.playTypeIds || []} onChange={v => set("playTypeIds", v)} testid="cf-play-types" helper />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-bold text-white">Optional</h3>
        <Field label="Typical minutes per game"><input value={value.minutes || ""} onChange={e => set("minutes", e.target.value)} placeholder="e.g. 18-22" className={`${inputCls} sm:w-56`} data-testid="cf-minutes" /></Field>
        <Field label="What would earn them more minutes?"><input value={value.earnMoreMinutes || ""} onChange={e => set("earnMoreMinutes", e.target.value)} className={inputCls} data-testid="cf-earn-more" /></Field>
        <Field label="Other comments"><textarea rows={3} value={value.comments || ""} onChange={e => set("comments", e.target.value)} className={inputCls} data-testid="cf-comments" /></Field>
      </section>

      <button type="submit" disabled={!canSubmit || submitting} className="w-full sm:w-auto px-6 py-3 bg-[var(--org-accent)] text-[#121212] text-xs uppercase tracking-[0.3em] font-bold disabled:opacity-40" data-testid="cf-submit">{submitting ? "Saving…" : submitLabel}</button>
      {!canSubmit && <div className="text-[10px] text-[#757575] -mt-4">Coach name, team, season and at least one answer are required.</div>}
    </form>
  );
}
